import { SNHUBLogicPublicData } from "@api-commons/snhub/SNHUBLogicPublicData";
import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { Utils } from "@ciaopeople/cp-shatsu/HTTP";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { isBoolean, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { isServicePathsMapping, MembershipConfig, NativeAppConfig, ServicePathsMapping } from "../../../config/PropertyConfig.js";

import { ApplicationUrlHandler } from "../ApplicationUrlHandler.js";

export interface UrlBuilderConfig {
    servicePaths: ServicePathsMapping,
    secure: boolean,
    host: string
}

export function isUrlBuilderConfig(obj: any): obj is UrlBuilderConfig {
    return isObject(obj) &&
            isServicePathsMapping(obj.servicePaths) &&
            isBoolean(obj.secure) &&
            isString(obj.host);
}


export class ApplicationUrlHandlerDefaultImpl implements ApplicationUrlHandler {

    private logger: Logger;
    

    constructor(private vw: DetectedWebView, 
                private snuLogic: SNHUBLogicPublicData,
                private nativeAppConfig: NativeAppConfig,
                private urlBuilderConfig: UrlBuilderConfig,
                private membershipConfig: MembershipConfig
    ) {

        this.logger = new BrowserLogger("[URLHND]", "#0000ae");
        if (!isUrlBuilderConfig(urlBuilderConfig)) {
            throw new Error("urlBuilderConfig must be instance of UrlBuilderConfig");
        }
    }

    public toDynamicLink(url: string) {
        const urlObj = new URL(url);
        return this.nativeAppConfig.dynamicLink.baseUrl + 
            "?link=" + encodeURIComponent(urlObj.toString()) + 
            "&apn=" + this.nativeAppConfig.dynamicLink.apn +
            "&amv=" + this.nativeAppConfig.dynamicLink.amv +
            "&ibi=" + this.nativeAppConfig.dynamicLink.ibi +
            "&isi=" + this.nativeAppConfig.dynamicLink.isi +
            "&ius=" + this.nativeAppConfig.dynamicLink.ius;
    }

    

    getProperUrl(url: string): Promise<string> {
        
        let retval: Promise<string> = Promise.resolve(url);
        if(this.vw.native_app) {
            this.logger.debug("Native app case giving back clean url", url);
        }
        else if (!this.nativeAppConfig.disabled) {
            if (this.vw.platform && this.vw.platform.android) {
                if ( this.vw.browser.webview ) {
                    this.logger.debug("We are in android webview case");
                    retval = this.snuLogic.getSNUTokenAnyways().then(tok => {
                        if(tok) {
                            this.logger.debug("Appending Merge Token");
                            return Utils.addParamsToUrl(url, { snumerge: tok });
                        }
                        else return url; 
                    }).then(
                        url => {
                            const rval = url.replace(/^https?/, "intent") + "#Intent;scheme=http;action=android.intent.action.VIEW;end;";
                            this.logger.debug("Returning intent (android wv): ", rval);
                            return rval;
                        }
                    );
                }
            }
            else if (this.vw.platform && this.vw.platform.ios && !this.membershipConfig?.enable) {
                this.logger.debug("We are in ios case");
                const rval = this.toDynamicLink(url);
                this.logger.debug("Returning intent (ios): ", rval);
                retval = Promise.resolve( rval );
            }
            else {
                this.logger.debug("We are in standard url case (app enabled)");
            }
        }
        else {
            this.logger.debug("We are in standard url case (app disabled)");
        }
        

        return retval;
    }
    openUrl(url: string, sameTarget?: boolean): void {
        if(this.vw.native_app) {
            this.logger.debug("Native App - setting document location to url:", url);
            document.location.href = url;
        }
        else if(!this.nativeAppConfig.disabled && this.vw.platform.ios) {
            document.location.href = url;
            setTimeout(() => {
                document.location.href = this.nativeAppConfig.ios_alternative_url;
            }, 400);
        }
        else {
            const intent = url.match(/^intent:/);
            if(sameTarget && !intent) document.location.href = url;
            else window.open(url);
        }
    }
    openSettingsUrl(settingsPage: string, utm_source?: string, utm_medium?: string, otherParams?: { [k: string]: string; }): void {
        const url = this.getSettingsUrl(settingsPage, utm_source, utm_medium, otherParams);
        this.getProperUrl(url).then(proper => {
            this.openUrl(proper);
        });

    }
    getSettingsUrl(settingsPage: string, utm_source?: string, utm_medium?: string, otherParams?: { [k: string]: string; }): string {
        const params = otherParams || {};
        if(utm_source) params.utm_source = utm_source;
        if(utm_medium) params.utm_medium = utm_medium;
        return this.buildServiceUrl(settingsPage, params); 
    }


    public buildServiceUrl(label: string, params?: { [k: string]: string; }): string {
        this.logger.debug("** this is mi config: ", this.urlBuilderConfig);
        this.logger.debug("[UTM]", params);

        if (label in this.urlBuilderConfig.servicePaths) {
            let s="";
            if(isObject(params)) {
                const ps = [];
                for(const key in params) {
                    ps.push(encodeURIComponent(key) + "=" + encodeURIComponent(params[key]));
                }
                if(ps.length) {
                    s = "?" + ps.join("&");
                }
            }
            return (this.urlBuilderConfig.secure?"https":"http") +"://" + this.urlBuilderConfig.host + this.urlBuilderConfig.servicePaths[label] + s;
        }
        else return "#";
    }

    
  
    public isServiceUrl(url: string): boolean {
        if (isString(url)) {
            const sep = Utils.urlSep(url);
            const comp = (this.urlBuilderConfig.secure?"https":"http") +"://" + this.urlBuilderConfig.host + this.urlBuilderConfig.servicePaths.basepath;
            const dividedHost = this.urlBuilderConfig.host.split(".");
            let comp2 = comp;
            if(dividedHost.length >= 2) {
                const h2 = dividedHost.slice(-2).join(".");
                comp2 = (this.urlBuilderConfig.secure?"https":"http") +"://" + h2 + this.urlBuilderConfig.servicePaths.basepath;
            }
            if (sep && sep.url) {
                const cc = sep.url.substring(0, comp.length);
                const cc2 = sep.url.substring(0, comp2.length);
                return cc == comp || cc2 == comp2;
            }
            else return false;
        }
        else return false;
    }
    
}